/**
 * Alt Yüklenici Ödemeleri (Faz 32 — Şubat 2026)
 * ==============================================
 * Taşeronlara yapılan ödemelerin kaydı ve takibi.
 * Şantiye → Ekip cascading, opsiyonel Sözleşme/Hakediş referansı.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import {
  Plus, PencilSimple, TrashSimple, MagnifyingGlass, CurrencyCircleDollar,
  Buildings, UsersThree, Bank, Receipt, HandCoins, CheckCircle, CaretRight,
  ListChecks, FileText, FilePdf, ArrowLeft, UploadSimple, DownloadSimple, FileXls,
  CaretUpDown, Check, CaretUp, CaretDown, ArrowsDownUp,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { smartTitleCaseTR } from "@/lib/textFormat";

// ---- MoneyInput (Turkish) ----
const formatTR = (s) => {
  if (s === "" || s == null) return "";
  const n = Number(String(s).replace(/\./g, "").replace(",", "."));
  if (!isFinite(n)) return "";
  const [intPart, decPart] = String(n).split(".");
  const withDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decPart ? `${withDots},${decPart}` : withDots;
};
const parseTR = (s) => String(s || "").replace(/\./g, "").replace(",", ".");
function MoneyInput({ value, onChange, "data-testid": testId, ...rest }) {
  const [display, setDisplay] = React.useState(() => formatTR(value));
  const focusedRef = React.useRef(false);
  React.useEffect(() => {
    if (!focusedRef.current) setDisplay(formatTR(value));
  }, [value]);
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^\d,.]/g, "");
    const canonical = parseTR(raw);
    const intPart = canonical.split(".")[0] || "";
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decMatch = raw.match(/,(\d*)$/);
    const out = raw.endsWith(",")
      ? intFormatted + ","
      : decMatch
        ? intFormatted + "," + decMatch[1]
        : intFormatted;
    setDisplay(out);
    onChange(canonical);
  };
  return (
    <Input type="text" inputMode="decimal" value={display}
           onFocus={() => { focusedRef.current = true; }}
           onBlur={() => { focusedRef.current = false; setDisplay(formatTR(value)); }}
           onChange={handleChange}
           data-testid={testId}
           {...rest} />
  );
}

const fmtTL = (n) => new Intl.NumberFormat("tr-TR", {
  style: "currency", currency: "TRY", maximumFractionDigits: 2,
}).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const METHOD_OPTS = [
  { v: "banka", label: "Banka Havalesi / EFT", icon: Bank },
  { v: "nakit", label: "Nakit", icon: HandCoins },
  { v: "cek", label: "Çek", icon: Receipt },
  { v: "senet", label: "Senet", icon: Receipt },
  { v: "kredi_karti", label: "Kredi Kartı", icon: Receipt },
];

const emptyForm = {
  subcontractor_id: "",
  site_id: "",
  contract_id: "",
  progress_id: "",
  payment_date: new Date().toISOString().slice(0, 10),
  amount: "",
  method: "banka",
  from_account: "",
  to_account: "",
  reference: "",
  description: "",
  notes: "",
  category_code: "",
};

// ---- CategoryCombobox — aranabilir bütçe kalemi seçici ----
function CategoryCombobox({ value, onChange, options, "data-testid": testId }) {
  const [open, setOpen] = React.useState(false);
  const selected = React.useMemo(
    () => options.find((c) => c.code === value),
    [options, value],
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="mt-1 w-full flex items-center justify-between h-10 px-3 border border-slate-300 rounded-none sm:rounded-sm bg-white text-sm text-left hover:border-[#1B4776] focus:outline-none focus:border-[#1B4776]"
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[10px] text-[#1B4776] font-bold shrink-0">
                {selected.code}
              </span>
              <span className="text-xs truncate">{selected.name}</span>
            </span>
          ) : (
            <span className="text-slate-400">Bütçe kalemi ara / seç... (opsiyonel)</span>
          )}
          <CaretUpDown size={14} className="opacity-50 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[520px] max-w-[calc(100vw-2rem)]" align="start">
        <Command
          filter={(itemValue, search) => {
            const q = (search || "").toLowerCase()
              .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
              .replace(/İ/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u");
            const target = (itemValue || "").toLowerCase()
              .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
              .replace(/İ/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u");
            return target.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Kod veya isim yazın... (Ör: taşeron elden)"
                        data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>Bütçe kalemi bulunamadı.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="- seçilmedi -"
                onSelect={() => { onChange(""); setOpen(false); }}
                data-testid={`${testId}-none`}
              >
                <Check size={14}
                       className={value ? "opacity-0 mr-2" : "mr-2 text-[#1B4776]"} />
                <span className="text-slate-500 italic text-xs">
                  — Seçilmedi (Bütçe kaydında uyarı ikonu görünür) —
                </span>
              </CommandItem>
              {options.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.code} ${c.name}`}
                  onSelect={() => { onChange(c.code); setOpen(false); }}
                  data-testid={`${testId}-item-${c.code}`}
                >
                  <Check size={14}
                         className={value === c.code ? "mr-2 text-[#1B4776]" : "opacity-0 mr-2"} />
                  <span className="font-mono text-[10px] text-[#1B4776] font-bold mr-2 shrink-0">
                    {c.code}
                  </span>
                  <span className="text-xs">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function SubPayments() {
  const { sites, activeSiteId } = useSites();
  const confirm = useConfirm();

  const [rows, setRows] = useState([]);
  const [totalAmt, setTotalAmt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [progresses, setProgresses] = useState([]);
  const [budgetCategories, setBudgetCategories] = useState([]);

  // Filters
  const [q, setQ] = useState("");
  const [fSite, setFSite] = useState(activeSiteId || "all");
  const [fSub, setFSub] = useState("all");
  const [fCon, setFCon] = useState("all");
  const [fMethod, setFMethod] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [sort, setSort] = useState({ key: "payment_date", dir: "desc" });
  const toggleSort = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
  );
  const sortedRows = useMemo(() => {
    const norm = (s) => (s || "").toString().trim().toLowerCase()
      .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
      .replace(/İ/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u");
    const arr = [...rows];
    const getVal = (r) => {
      switch (sort.key) {
        case "amount": return Number(r.amount) || 0;
        case "payment_date": return r.payment_date || "";
        case "method": return norm(r.method);
        case "site_code": return norm(r.site_code);
        case "subcontractor_name": return norm(r.subcontractor_name);
        case "contract_no": return norm(r.contract_no || r.contract_title);
        case "reference": return norm(r.reference);
        default: return "";
      }
    };
    arr.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sort]);

  // Dialog state
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Import (bulk Excel) state
  const [impOpen, setImpOpen] = useState(false);
  const [impSite, setImpSite] = useState("");
  const [impFile, setImpFile] = useState(null);
  const [impBusy, setImpBusy] = useState(false);
  const [impErrors, setImpErrors] = useState([]);

  useEffect(() => { setFSite(activeSiteId || "all"); }, [activeSiteId]);

  const loadSubs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subcontractors`);
      setSubs(Array.isArray(data) ? data : (data.items || []));
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  const loadContracts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subcontracts`);
      setContracts(data || []);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  const loadBudgetCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/budget/categories`,
        { params: { kind: "gider" } });
      setBudgetCategories(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, []);
  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fSite !== "all") params.site_id = fSite;
      if (fSub !== "all") params.subcontractor_id = fSub;
      if (fCon !== "all") params.contract_id = fCon;
      if (fMethod !== "all") params.method = fMethod;
      if (fFrom) params.date_from = fFrom;
      if (fTo) params.date_to = fTo;
      if (q) params.q = q;
      const { data } = await axios.get(`${API}/sub-payments`, { params });
      setRows(data.items || []);
      setTotalAmt(data.total_amount || 0);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [fSite, fSub, fCon, fMethod, fFrom, fTo, q]);

  useEffect(() => { loadSubs(); loadContracts(); loadBudgetCategories(); }, [loadSubs, loadContracts, loadBudgetCategories]);
  useEffect(() => { loadRows(); }, [loadRows]);

  // ---- Excel Bulk Import ----
  const openImport = () => {
    setImpSite(fSite !== "all" ? fSite : "");
    setImpFile(null);
    setImpErrors([]);
    setImpOpen(true);
  };
  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/sub-payments/template.xlsx`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "taseron_odeme_sablonu.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const doImport = async () => {
    if (!impSite) { toast.error("Şantiye seçin"); return; }
    if (!impFile) { toast.error("Excel dosyası seçin"); return; }
    setImpBusy(true);
    setImpErrors([]);
    try {
      const fd = new FormData();
      fd.append("file", impFile);
      const { data } = await axios.post(
        `${API}/sub-payments/import?site_id=${impSite}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const inserted = data.inserted || 0;
      const errs = data.errors || [];
      if (inserted > 0) {
        toast.success(`${inserted} ödeme aktarıldı${errs.length ? ` · ${errs.length} hatalı satır` : ""}`);
      } else if (errs.length) {
        toast.error(`Aktarım başarısız · ${errs.length} hatalı satır`);
      } else {
        toast.info("İçe aktarılacak veri bulunamadı");
      }
      setImpErrors(errs);
      if (inserted > 0 && errs.length === 0) {
        setImpOpen(false);
        setImpFile(null);
      }
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setImpBusy(false); }
  };

  // Cascading: subs on selected site
  const subsForSite = useMemo(() => {
    if (fSite === "all") return subs;
    return subs.filter((s) => (s.site_ids || []).includes(fSite));
  }, [subs, fSite]);
  // Reset sub filter if site changes and current sub not in list
  useEffect(() => {
    if (fSub !== "all" && !subsForSite.find((s) => s.id === fSub)) setFSub("all");
  }, [subsForSite, fSub]);
  const contractsFor = useMemo(() => {
    return contracts.filter((c) =>
      (fSite === "all" || c.site_id === fSite) &&
      (fSub === "all" || c.subcontractor_id === fSub),
    );
  }, [contracts, fSite, fSub]);
  useEffect(() => {
    if (fCon !== "all" && !contractsFor.find((c) => c.id === fCon)) setFCon("all");
  }, [contractsFor, fCon]);

  // ---- Dialog form: cascading same pattern ----
  const dlgSubs = useMemo(() => {
    if (!form.site_id) return [];
    return subs.filter((s) => (s.site_ids || []).includes(form.site_id));
  }, [subs, form.site_id]);
  const dlgContracts = useMemo(() => {
    if (!form.subcontractor_id) return [];
    return contracts.filter(
      (c) => c.subcontractor_id === form.subcontractor_id
        && c.site_id === form.site_id,
    );
  }, [contracts, form.subcontractor_id, form.site_id]);

  // Load progresses for selected contract in dialog
  useEffect(() => {
    if (!form.contract_id) { setProgresses([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/subprogress`, {
          params: { contract_id: form.contract_id },
        });
        setProgresses(data || []);
      } catch { setProgresses([]); }
    })();
  }, [form.contract_id]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      site_id: fSite !== "all" ? fSite : "",
      subcontractor_id: fSub !== "all" ? fSub : "",
      contract_id: fCon !== "all" ? fCon : "",
    });
    setDlgOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      subcontractor_id: r.subcontractor_id,
      site_id: r.site_id,
      contract_id: r.contract_id || "",
      progress_id: r.progress_id || "",
      payment_date: r.payment_date,
      amount: String(r.amount || ""),
      method: r.method,
      from_account: r.from_account || "",
      to_account: r.to_account || "",
      reference: r.reference || "",
      description: r.description || "",
      notes: r.notes || "",
      category_code: r.category_code || "",
    });
    setDlgOpen(true);
  };

  const save = async () => {
    if (!form.site_id) return toast.error("Şantiye seçin");
    if (!form.subcontractor_id) return toast.error("Ekip / Taşeron seçin");
    if (!(Number(form.amount) > 0)) return toast.error("Ödeme tutarı 0'dan büyük olmalı");
    try {
      const payload = {
        ...form,
        description: smartTitleCaseTR(form.description || ""),
        notes: smartTitleCaseTR(form.notes || ""),
        amount: Number(form.amount),
      };
      if (editing) {
        await axios.put(`${API}/sub-payments/${editing}`, payload);
        toast.success("Ödeme güncellendi");
      } else {
        await axios.post(`${API}/sub-payments`, payload);
        toast.success("Ödeme kaydedildi");
      }
      setDlgOpen(false);
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (r) => {
    const ok = await confirm({
      title: "Ödeme Silinsin mi?",
      description: `${r.subcontractor_name} — ${fmtTL(r.amount)} tutarındaki ödeme silinecek.`,
      confirmText: "Sil", variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/sub-payments/${r.id}`);
      toast.success("Ödeme silindi");
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Shell
      title="Yapılan Ödemeler"
      subtitle="Alt Yüklenici Yönetimi › Ödemeler"
      actions={
        <Link to="/taseron/odemeler">
          <Button variant="outline" className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest"
                  data-testid="sub-payments-back">
            <ArrowLeft size={14} className="mr-1.5" /> Ödeme Tipleri
          </Button>
        </Link>
      }
    >
      <div className="space-y-4" data-testid="sub-payments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase font-bold">
              Alt Yüklenici Yönetimi
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1B4776] flex items-center gap-2 mt-1">
              <CurrencyCircleDollar size={26} weight="duotone" className="text-emerald-700" />
              Ödemeler
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Taşeron ekiplerine yapılan ödemelerin kaydı — banka, nakit, çek, senet, kart.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  if (fSite !== "all") params.set("site_id", fSite);
                  if (fSub !== "all") params.set("subcontractor_id", fSub);
                  if (fCon !== "all") params.set("contract_id", fCon);
                  if (fMethod !== "all") params.set("method", fMethod);
                  if (fFrom) params.set("date_from", fFrom);
                  if (fTo) params.set("date_to", fTo);
                  if (q) params.set("q", q);
                  const res = await axios.get(
                    `${API}/sub-payments/report.pdf?${params.toString()}`,
                    { responseType: "blob" },
                  );
                  const blob = new Blob([res.data], { type: "application/pdf" });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `odemeler_${new Date().toISOString().slice(0, 10)}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("PDF indirildi");
                } catch (e) { toast.error(formatErr(e)); }
              }}
              disabled={rows.length === 0}
              variant="outline"
              className="rounded-none sm:rounded-sm"
              data-testid="sp-export-pdf">
              <FilePdf size={14} weight="bold" className="mr-1 text-rose-600" />
              PDF İndir
            </Button>
            <Button onClick={openImport}
                    variant="outline"
                    className="rounded-none sm:rounded-sm"
                    data-testid="sp-import-btn">
              <UploadSimple size={14} weight="bold" className="mr-1 text-slate-600" />
              Excel İçe Aktar
            </Button>
            <Button onClick={openCreate}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none sm:rounded-sm"
                    data-testid="sp-create-btn">
              <Plus size={14} weight="bold" className="mr-1" /> Yeni Ödeme
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Ödeme Sayısı" value={rows.length}
                    icon={ListChecks} accent="text-[#1B4776]" />
          <StatCard label="Toplam Ödeme" value={fmtTL(totalAmt)}
                    icon={CurrencyCircleDollar} accent="text-emerald-700" />
          <StatCard label="Farklı Ekip"
                    value={new Set(rows.map((r) => r.subcontractor_id)).size}
                    icon={UsersThree} accent="text-amber-700" />
          <StatCard label="Farklı Sözleşme"
                    value={new Set(rows.filter((r) => r.contract_id).map((r) => r.contract_id)).size}
                    icon={FileText} accent="text-slate-700" />
        </div>

        {/* Filters */}
        <Card className="p-3 rounded-none sm:rounded-sm border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <div className="col-span-2 md:col-span-2 relative">
              <MagnifyingGlass size={14}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Referans / açıklama / IBAN ara..."
                     className="pl-7 rounded-none sm:rounded-sm"
                     data-testid="sp-search" />
            </div>
            <Select value={fSite} onValueChange={setFSite}>
              <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sp-f-site">
                <Buildings size={12} weight="duotone" className="text-[#1B4776] mr-1" />
                <SelectValue placeholder="Şantiye" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şantiyeler</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="font-mono text-xs mr-2">{s.code}</span>{s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={fSub} onValueChange={setFSub}>
              <SelectTrigger className="rounded-none sm:rounded-sm"
                             disabled={fSite === "all"} data-testid="sp-f-sub">
                <UsersThree size={12} weight="duotone" className="text-[#556B10] mr-1" />
                <SelectValue placeholder={fSite === "all" ? "Önce şantiye" : "Ekip"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Ekipler</SelectItem>
                {subsForSite.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
                   className="rounded-none sm:rounded-sm" data-testid="sp-f-from" />
            <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)}
                   className="rounded-none sm:rounded-sm" data-testid="sp-f-to" />
          </div>
        </Card>

        {/* Table */}
        <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <SortableTh label="Tarih" k="payment_date" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Şantiye" k="site_code" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Ekip" k="subcontractor_name" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Sözleşme / İş" k="contract_no" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Yöntem" k="method" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <th className="px-3 py-2 text-left">Hesap Bilgileri</th>
                  <SortableTh label="Referans" k="reference" sort={sort} onSort={toggleSort} className="px-3 py-2 text-left" />
                  <SortableTh label="Tutar" k="amount" sort={sort} onSort={toggleSort} className="px-3 py-2 text-right" align="right" />
                  <th className="px-3 py-2 text-right w-20">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="text-center py-6 text-slate-500">Yükleniyor...</td></tr>
                )}
                {!loading && sortedRows.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-10 text-slate-400">
                    Ödeme kaydı yok. "Yeni Ödeme" ile başlayın.
                  </td></tr>
                )}
                {sortedRows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`sp-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{fmtDate(r.payment_date)}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[10px] font-bold text-[#1B4776]">{r.site_code}</div>
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      <div>{r.subcontractor_name}</div>
                      {r.subcontractor_contact && (
                        <div className="text-[10px] text-slate-500 font-normal mt-0.5 flex items-center gap-1">
                          <UsersThree size={10} weight="duotone" className="text-[#556B10]" />
                          <span className="font-bold text-[#1B4776]">{r.subcontractor_contact}</span>
                          {r.subcontractor_contact_title && (
                            <span className="text-slate-400">— {r.subcontractor_contact_title}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.contract_code ? (
                        <>
                          <div className="font-mono text-[10px] text-[#1B4776]">{r.contract_code}</div>
                          <div className="text-slate-600">{r.contract_title}</div>
                          {r.progress_no && (
                            <div className="text-[9px] text-amber-700 font-bold">Hakediş #{r.progress_no}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">— Sözleşme dışı —</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-50 text-emerald-800 rounded-sm">
                        {r.method_label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[10px] font-mono">
                      {r.from_account ? (
                        <div className="text-slate-600">
                          <span className="text-[9px] uppercase tracking-widest text-slate-400 font-sans font-bold mr-1">Kaynak:</span>
                          {r.from_account}
                        </div>
                      ) : (
                        <div className="text-slate-400 italic text-[10px]">Kaynak —</div>
                      )}
                      {r.to_account ? (
                        <div className="text-slate-700 mt-0.5">
                          <span className="text-[9px] uppercase tracking-widest text-emerald-700 font-sans font-bold mr-1">Alıcı:</span>
                          {r.to_account}
                        </div>
                      ) : (
                        <div className="text-slate-400 italic text-[10px] mt-0.5">Alıcı —</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-600">
                      {r.reference || <span className="text-slate-400 italic">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtTL(r.amount)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(r)}
                                className="p-1.5 text-slate-500 hover:text-[#1B4776] hover:bg-slate-100 rounded-sm"
                                data-testid={`sp-edit-${r.id}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => del(r)}
                                className="p-1.5 text-slate-500 hover:text-rose-700 hover:bg-rose-50 rounded-sm"
                                data-testid={`sp-delete-${r.id}`}>
                          <TrashSimple size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Yeni/Düzenle Dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-4xl rounded-none sm:rounded-sm max-h-[92vh] overflow-y-auto"
                       data-testid="sp-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B4776] flex items-center gap-2">
              <CurrencyCircleDollar size={20} weight="duotone" className="text-emerald-700" />
              {editing ? "Ödeme Düzenle" : "Yeni Ödeme Kaydı"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Adım 1: Şantiye → Ekip → (opsiyonel Sözleşme/Hakediş) */}
            <Card className="p-3 rounded-none sm:rounded-sm border-slate-200 bg-slate-50">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 flex items-center gap-2">
                <span className="bg-[#1B4776] text-white px-1.5 py-0.5 text-[9px]">1</span>
                Şantiye ve Ekip Seçimi
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Şantiye *</Label>
                  <Select value={form.site_id}
                          onValueChange={(v) => setForm({
                            ...form, site_id: v,
                            subcontractor_id: "", contract_id: "", progress_id: "",
                          })}>
                    <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sp-form-site">
                      <SelectValue placeholder="Önce şantiye seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="font-mono text-xs mr-2">{s.code}</span>{s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ekip / Taşeron *</Label>
                  <div className="border border-slate-300 bg-white max-h-40 overflow-y-auto">
                    {!form.site_id && (
                      <div className="p-3 text-center text-[11px] text-slate-400 italic">
                        Önce şantiye seçin
                      </div>
                    )}
                    {form.site_id && dlgSubs.length === 0 && (
                      <div className="p-3 text-center text-[11px] text-rose-600">
                        Bu şantiyeye atanmış ekip yok
                      </div>
                    )}
                    {form.site_id && dlgSubs.map((s) => (
                      <button key={s.id} type="button"
                        onClick={() => setForm({
                          ...form, subcontractor_id: s.id, contract_id: "", progress_id: "",
                        })}
                        className={`w-full text-left px-3 py-1.5 border-b border-slate-100 flex items-center gap-2 transition-colors ${
                          form.subcontractor_id === s.id
                            ? "bg-[#F7FBE9] border-l-4 border-l-[#95C11F]"
                            : "hover:bg-slate-50"
                        }`}
                        data-testid={`sp-form-sub-${s.id}`}>
                        <UsersThree size={12} weight="duotone" className="text-[#556B10]" />
                        <span className="text-xs font-bold flex-1">{s.name}</span>
                        {form.subcontractor_id === s.id && (
                          <CaretRight size={12} weight="bold" className="text-[#95C11F]" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {form.subcontractor_id && dlgContracts.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label>Hangi İşi İçin (Sözleşme — opsiyonel)</Label>
                    <Select value={form.contract_id || "__none"}
                            onValueChange={(v) => setForm({
                              ...form, contract_id: v === "__none" ? "" : v, progress_id: "",
                            })}>
                      <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sp-form-contract">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— Sözleşme dışı ödeme —</SelectItem>
                        {dlgContracts.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="font-mono text-[10px] mr-1">{c.code}</span> {c.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.contract_id && progresses.length > 0 && (
                    <div>
                      <Label>Hakediş (opsiyonel)</Label>
                      <Select value={form.progress_id || "__none"}
                              onValueChange={(v) => setForm({
                                ...form, progress_id: v === "__none" ? "" : v,
                              })}>
                        <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sp-form-progress">
                          <SelectValue placeholder="Hakediş seçin (opsiyonel)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">— Belirli hakedişe bağlı değil —</SelectItem>
                          {progresses.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              Hakediş #{p.no} — Net: {fmtTL(p.net_payable)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Adım 2: Ödeme Bilgileri */}
            <Card className="p-3 rounded-none sm:rounded-sm border-slate-200">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 flex items-center gap-2">
                <span className="bg-[#1B4776] text-white px-1.5 py-0.5 text-[9px]">2</span>
                Ödeme Bilgileri
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Ödeme Tarihi *</Label>
                  <Input type="date" value={form.payment_date}
                         onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                         className="rounded-none sm:rounded-sm" data-testid="sp-form-date" />
                </div>
                <div className="col-span-2">
                  <Label>Ödeme Tutarı ₺ *</Label>
                  <MoneyInput value={form.amount}
                              onChange={(v) => setForm({ ...form, amount: v })}
                              className="font-mono text-right font-bold text-lg rounded-none sm:rounded-sm"
                              data-testid="sp-form-amount" />
                </div>
                <div className="col-span-3">
                  <Label>Ödeme Yöntemi</Label>
                  <div className="grid grid-cols-5 gap-2 mt-1">
                    {METHOD_OPTS.map((m) => {
                      const Icon = m.icon;
                      const active = form.method === m.v;
                      return (
                        <button key={m.v} type="button"
                          onClick={() => setForm({ ...form, method: m.v })}
                          className={`border-2 p-2 flex flex-col items-center gap-1 transition-colors ${
                            active
                              ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                              : "border-slate-200 hover:border-slate-300 text-slate-600"
                          }`}
                          data-testid={`sp-form-method-${m.v}`}>
                          <Icon size={16} weight={active ? "fill" : "duotone"} />
                          <span className="text-[10px] font-bold uppercase text-center leading-tight">
                            {m.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label>Kaynak Hesap (Bizim)</Label>
                  <Input value={form.from_account}
                         onChange={(e) => setForm({ ...form, from_account: e.target.value })}
                         placeholder="Örn: Akbank TR12 3456 7890 ..."
                         className="rounded-none sm:rounded-sm font-mono text-xs"
                         data-testid="sp-form-from" />
                </div>
                <div className="col-span-2">
                  <Label>Alıcı Hesap / IBAN (Taşeronun) *</Label>
                  <Input value={form.to_account}
                         onChange={(e) => setForm({ ...form, to_account: e.target.value })}
                         placeholder="Örn: TR98 0006 1005 1234 5678 9012 34"
                         className="rounded-none sm:rounded-sm font-mono text-xs"
                         data-testid="sp-form-to" />
                </div>
                <div>
                  <Label>Referans / Dekont No</Label>
                  <Input value={form.reference}
                         onChange={(e) => setForm({ ...form, reference: e.target.value })}
                         placeholder="EFT-2026-01234"
                         className="rounded-none sm:rounded-sm font-mono text-xs"
                         data-testid="sp-form-ref" />
                </div>
                <div className="col-span-2">
                  <Label>Açıklama</Label>
                  <Input value={form.description}
                         onChange={(e) => setForm({ ...form, description: e.target.value })}
                         placeholder="Örn: Şubat ayı hakediş ödemesi"
                         className="rounded-none sm:rounded-sm"
                         data-testid="sp-form-desc" />
                </div>
                <div className="col-span-3">
                  <Label className="flex items-center gap-2">
                    Gider Kodu / Bütçe Kalemi
                    <span className="text-[10px] font-normal text-slate-400 normal-case tracking-normal">
                      (opsiyonel — boş bırakılırsa Bütçe Yönetimi'nde kırmızı uyarı gösterilir)
                    </span>
                  </Label>
                  <CategoryCombobox
                    value={form.category_code}
                    onChange={(code) => setForm({ ...form, category_code: code })}
                    options={budgetCategories}
                    data-testid="sp-form-category"
                  />
                </div>
                <div className="col-span-3">
                  <Label>Notlar</Label>
                  <Textarea rows={2} value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="rounded-none sm:rounded-sm" data-testid="sp-form-notes" />
                </div>
              </div>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}
                    className="rounded-none sm:rounded-sm">Vazgeç</Button>
            <Button onClick={save}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none sm:rounded-sm"
                    data-testid="sp-form-save">
              <CheckCircle size={14} weight="bold" className="mr-1" />
              {editing ? "Güncelle" : "Ödeme Kaydını Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel Bulk Import Dialog */}
      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-lg" data-testid="sp-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold font-heading flex items-center gap-2">
              <FileXls size={18} className="text-emerald-700" />
              Excel ile Toplu Taşeron Ödemesi
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Seçili şantiyeye atanmış taşeronlar için toplu ödeme kaydı oluşturulur.
              Şablonu indirip doldurun, ardından yükleyin. Firma adı şablondaki isimle
              (büyük/küçük harf duyarsız) eşleştirilir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Button variant="outline" onClick={downloadTemplate}
                      data-testid="sp-imp-template-btn"
                      className="rounded-none sm:rounded-sm h-9 w-full">
                <DownloadSimple size={14} className="mr-1.5" />
                <span className="text-xs uppercase tracking-widest font-bold">Excel Şablonu İndir</span>
              </Button>
            </div>
            <div>
              <Label>Şantiye *</Label>
              <Select value={impSite} onValueChange={setImpSite}>
                <SelectTrigger className="rounded-none sm:rounded-sm mt-1" data-testid="sp-imp-site">
                  <SelectValue placeholder="Seçin" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="font-mono text-xs text-[#1B4776] mr-2">{s.code}</span>{s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Excel Dosyası (.xlsx) *</Label>
              <Input type="file" accept=".xlsx,.xlsm"
                     onChange={(e) => setImpFile(e.target.files?.[0] || null)}
                     data-testid="sp-imp-file"
                     className="rounded-none sm:rounded-sm mt-1" />
              <div className="text-[10px] text-slate-500 mt-1">
                Kolonlar: Firma Adı · Tarih · Tutar · Yöntem · Bizim Hesap · Taşeron IBAN · Dekont · Açıklama · Notlar
              </div>
            </div>
            {impErrors.length > 0 && (
              <div className="border border-rose-200 bg-rose-50 p-2 max-h-40 overflow-y-auto rounded-none sm:rounded-sm"
                   data-testid="sp-imp-errors">
                <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700 mb-1">
                  Hatalı Satırlar ({impErrors.length})
                </div>
                <ul className="text-[11px] text-rose-800 space-y-0.5">
                  {impErrors.slice(0, 30).map((er, idx) => (
                    <li key={idx}>Satır {er.row}: {er.error}</li>
                  ))}
                  {impErrors.length > 30 && (
                    <li className="italic text-rose-600">…ve {impErrors.length - 30} satır daha</li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpOpen(false)}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={doImport} disabled={impBusy} data-testid="sp-imp-submit"
                    className="rounded-none sm:rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white">
              <UploadSimple size={14} className="mr-1.5" />
              <span className="text-xs uppercase tracking-widest font-bold">
                {impBusy ? "Yükleniyor…" : "İçe Aktar"}
              </span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{children}</div>;
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-3 rounded-none sm:rounded-sm border-slate-200">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center justify-between">
        <span>{label}</span>
        {Icon && <Icon size={14} weight="duotone" className={accent} />}
      </div>
      <div className={`text-2xl font-bold mt-1 ${accent || "text-slate-800"}`}>{value}</div>
    </Card>
  );
}


function SortableTh({ label, k, sort, onSort, className = "", align = "left" }) {
  const active = sort.key === k;
  const dir = active ? sort.dir : null;
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-widest font-bold ${
          active ? "text-[#1B4776]" : "text-slate-500"
        } hover:text-[#1B4776]`}
        style={{ justifyContent: align === "right" ? "flex-end" : "flex-start", width: "100%" }}
      >
        {label}
        {dir === "asc" && <CaretUp size={10} weight="bold" />}
        {dir === "desc" && <CaretDown size={10} weight="bold" />}
        {!active && <ArrowsDownUp size={10} weight="bold" className="opacity-30" />}
      </button>
    </th>
  );
}
